import { useEffect, useState } from 'react';
import { aaiApi } from '../services/aaiApi';
import type { AaiContext } from '../types/aai';
import { seedPermissions } from '../lib/permissionSeed';

// Resolves the signed-in user's AAI context (GET /api/aai/me). The frontend only
// knows the coarse SYSTEM role from the token (client/consultant/…); the AAI
// workspace role + scope + department + capabilities come from here. Participants
// and department heads get a stripped-down app, so the sidebar, dashboard, and
// the promoted department/roster pages all branch on this.
//
// One fetch is shared across consumers via an in-flight dedupe + a cache keyed
// by the signed-in email (so a user switch invalidates it). A localStorage seed
// of the role gives an instant, flicker-free first paint of the nav on repeat
// loads; the full context (department, capabilities) arrives with the fetch.

let cacheKey: string | null = null;
let cacheVal: AaiContext | null = null;
let inflight: Promise<AaiContext | null> | null = null;

const SEED_KEY = 'aai_workspace_role';
const PROVIDER_SEED_KEY = 'is_provider';

function whoAmI(): string {
  return localStorage.getItem('user_email') || '';
}

function isClientLike(): boolean {
  const sys = localStorage.getItem('user_role');
  return sys === 'client' || sys === 'client_admin';
}

async function fetchContext(): Promise<AaiContext | null> {
  const who = whoAmI();
  if (cacheKey === who) return cacheVal;
  if (inflight) return inflight;
  inflight = aaiApi
    .getContext(true)
    .then((r) => { cacheKey = who; cacheVal = r.data ?? null; return cacheVal; })
    .catch(() => { cacheKey = who; cacheVal = null; return null; })
    .finally(() => { inflight = null; });
  return inflight;
}

export interface ParticipantState {
  isParticipant: boolean;
  isDepartmentHead: boolean;
  /** Belongs to a company with company_kind='provider' — AA itself, or any
   *  client promoted to one later. Provider members are STAFF, so a provider
   *  participant keeps the workspace a customer participant never sees. */
  isProvider: boolean;
  role: string | null;
  context: AaiContext | null;
  ready: boolean;
}

export function useParticipant(): ParticipantState {
  const clientLike = isClientLike();
  const cached = cacheKey === whoAmI();
  const seedRole = clientLike ? localStorage.getItem(SEED_KEY) : null;
  // Mirrored from /aai/me on every load (below), so on a repeat load the role
  // seed and the provider seed land together — the nav never paints a role
  // whose provider status it does not yet know.
  let seedProvider = false;
  if (clientLike) seedProvider = localStorage.getItem(PROVIDER_SEED_KEY) === '1';

  const [context, setContext] = useState<AaiContext | null>(cached ? cacheVal : null);
  const [role, setRole] = useState<string | null>(cached ? (cacheVal?.workspace_role ?? null) : seedRole);
  // Render immediately when we have a confirmed cache, a seeded role to trust,
  // or the user isn't a client at all; otherwise hold until /me resolves.
  const [ready, setReady] = useState<boolean>(!clientLike || cached || seedRole !== null);

  useEffect(() => {
    if (!clientLike) { setReady(true); return; }
    let alive = true;
    fetchContext().then((ctx) => {
      if (!alive) return;
      const r = ctx?.workspace_role ?? null;
      setContext(ctx);
      setRole(r);
      setReady(true);
      if (r) localStorage.setItem(SEED_KEY, r);
      else localStorage.removeItem(SEED_KEY);
      // Provider-lane flags come fresh from /aai/me — mirror them to localStorage
      // so the sidebar "Builder" link (and its /admin vs /expert destination)
      // works for ANY login method (email or Google) and refreshes each load,
      // reflecting a toggle without a re-login.
      const p = ctx as any;
      if (p) {
        localStorage.setItem(PROVIDER_SEED_KEY, p.is_provider ? '1' : '');
        localStorage.setItem('is_provider_owner', p.is_provider_owner ? '1' : '');
        localStorage.setItem('is_builder_admin', p.is_builder_admin ? '1' : '');
        localStorage.setItem('is_expert', p.is_expert ? '1' : '');
        localStorage.setItem('provider_id', p.provider_id || '');
        localStorage.setItem('is_main_provider', p.is_main_provider ? '1' : '');
        localStorage.setItem('serving_provider_name', p.serving_provider_name || '');
        if (p.company_name) localStorage.setItem('client_name', p.company_name);
      }
      // G3. The permission list is mirrored alongside the role, so the
      // sidebar's first paint on a reload has both and never flashes items
      // in or out.
      seedPermissions(ctx?.permissions);
    });
    return () => { alive = false; };
  }, [clientLike]);

  let isProvider = seedProvider;
  if (context) isProvider = !!context.is_provider;

  return {
    isParticipant: role === 'AI_PARTICIPANT',
    isDepartmentHead: role === 'DEPARTMENT_HEAD',
    isProvider,
    role,
    context,
    ready,
  };
}
